/**
 * reco-page.js - "Preporuceno" page: profile from browser activity, the graph row
 * ("Goes with your cards", scored by reco-graph.js), "Continue where you left off",
 * "Customers who bought your cards also buy", and the "Popular" fallback.
 * Pure functions live on window.MtgRecoPage so they can be tested in Node.
 *
 * Snapshot (mtgkarte_reco_snap_v1): the finished rows are kept in localStorage. warm() computes
 * them on OTHER pages while the visitor browses (loaded by assets/reco-warm.js), the inline
 * painter in sections/reco-page.liquid draws them the moment the HTML is parsed, and the
 * controller below adopts those rows, then recomputes in the background and swaps in the
 * fresh list only if the visitor has not touched the page yet.
 */
(function () {
  'use strict';
  var W = typeof window !== 'undefined' ? window : globalThis;
  if (W.MtgRecoPage) return;

  var DAY = 24 * 3600 * 1000;
  var PAGE_SIZE = 24;     // graph row: cards shown per page / per "Load more"
  var GRAPH_LIMIT = 200;  // graph row: cards computed up front (paged locally, no extra fetches)
  var GRAPH_MIN = 6;      // fewer than this -> Popular fallback instead

  function freshness(t, now) {
    var age = now - t;
    if (age < DAY) return 1.0;
    if (age < 3 * DAY) return 0.6;
    if (age < 7 * DAY) return 0.3;
    return 0;
  }
  var INTEREST_MIN = 0.15; // = reco-tracker.js: below this a view is a glance
  var CART_WEIGHT = 4;
  // A view is worth at most half of a card in the cart: interest (0..1, reco-tracker.js,
  // already faded by time) x VIEW_WEIGHT. The interest score itself is not touched.
  var VIEW_WEIGHT = 2;
  function isGlance(entry) {
    return entry.kind === 'viewed' && typeof entry.i === 'number' && entry.i < INTEREST_MIN;
  }
  function itemWeight(entry, now) {
    if (entry.kind === 'viewed' && typeof entry.i === 'number') return VIEW_WEIGHT * entry.i;
    // cart contents are the strongest signal (4), then removed-from-cart (3), viewed twice+ (2), viewed once (1)
    var kind = entry.kind === 'cart' ? CART_WEIGHT : entry.kind === 'removed' ? 3 : (entry.c || 1) >= 2 ? 2 : 1;
    return kind * freshness(entry.t, now);
  }
  function inc(map, key, w) { map.set(key, (map.get(key) || 0) + w); }

  // Profile = weighted list of handles the visitor interacted with (viewed / removed / cart).
  // Viewed entries may carry `i` = current interest 0..1 (added on the page from reco-tracker.js).
  function buildProfile(store, now) {
    var pr = { items: [], seen: new Set() };
    var entries = [];
    var viewed = (store && store.viewed) || {};
    Object.keys(viewed).forEach(function (h) { entries.push({ handle: h, kind: 'viewed', c: viewed[h].c, t: viewed[h].t, i: viewed[h].i, d: viewed[h].d }); });
    ((store && store.removed) || []).forEach(function (r) { entries.push({ handle: r.h, kind: 'removed', t: r.t }); });
    ((store && store.cart) || []).forEach(function (h) { entries.push({ handle: h, kind: 'cart', t: now }); });
    entries.forEach(function (e) {
      var w = itemWeight(e, now);
      if (w <= 0) return;
      pr.seen.add(e.handle);
      pr.items.push({ handle: e.handle, w: w, t: e.t, glance: isGlance(e), kind: e.kind, c: e.c, d: e.d, i: e.i });
    });
    return pr;
  }

  // Seeds for the graph row, strongest first. Glances only when there is too little else.
  function seedHandles(profile) {
    var sorted = profile.items.slice().sort(function (a, b) { return b.w - a.w; });
    var real = sorted.filter(function (i) { return !i.glance; });
    return (real.length >= 3 ? real : sorted).map(function (i) { return i.handle; });
  }

  // Graph multiplier per profile seed, relative to a card in the cart (= 1): a card viewed
  // many times still pulls its partners in, but below the ones that go with the cart.
  function seedWeights(profile) {
    var out = {};
    profile.items.forEach(function (i) { out[i.handle] = Math.max(out[i.handle] || 0, Math.min(1, i.w / CART_WEIGHT)); });
    return out;
  }

  function topKeys(map, n) {
    return Array.from(map.entries()).sort(function (a, b) { return b[1] - a[1]; }).slice(0, n).map(function (e) { return e[0]; });
  }

  function copurchaseRow(profile, pairs, opts) {
    var limit = (opts && opts.limit) || 12; var exclude = (opts && opts.exclude) || new Set();
    var score = new Map();
    profile.items.forEach(function (it) {
      (pairs[it.handle] || []).forEach(function (p) {
        if (exclude.has(p[0]) || profile.seen.has(p[0])) return;
        inc(score, p[0], p[1] * it.w);
      });
    });
    return topKeys(score, limit);
  }

  // "Continue where you left off": what the visitor actually looked into, newest first (no glances).
  function continueRow(profile, opts) {
    var limit = (opts && opts.limit) || 12;
    return profile.items.filter(function (i) { return !i.glance; }).sort(function (a, b) { return b.t - a.t; }).slice(0, limit).map(function (i) { return i.handle; });
  }

  // Local paging over an already computed list: next() returns the next `size`
  // items (first call = the first page), done() tells whether anything is left.
  function pager(items, size) {
    var list = items || [], n = size || PAGE_SIZE, pos = 0;
    return {
      next: function () { var out = list.slice(pos, pos + n); pos += out.length; return out; },
      done: function () { return pos >= list.length; },
      shown: function () { return pos; }
    };
  }

  // deck-index.json rows: [title, handle, imageTail, [[variantId, price, variantTitle, qty], ...]]
  // (only variants that are available). Returns { map: handle -> row, imgBase }.
  function buildDeckLookup(idx) {
    var map = new Map();
    if (idx && idx.rows) idx.rows.forEach(function (r) { map.set(r[1], r); });
    return { map: map, imgBase: (idx && idx.imgBase) || '' };
  }

  // Same shape as the engine's normalizeProduct(): variant preference Near Mint,
  // then Near Mint - Foil, then the cheapest available; price in minor units.
  function itemFromDeckRow(deck, row) {
    var vs = row[3] || [];
    if (!vs.length) return null;
    var nm = null, nmFoil = null, cheapest = null;
    vs.forEach(function (v) {
      var t = String(v[2] || '').trim();
      if (!nm && /^near mint$/i.test(t)) nm = v;
      else if (!nmFoil && /^near mint/i.test(t)) nmFoil = v;
      if (!cheapest || v[1] < cheapest[1]) cheapest = v;
    });
    var chosen = nm || nmFoil || cheapest;
    var currency = 'RSD';
    try { if (W.Shopify && W.Shopify.currency && W.Shopify.currency.active) currency = W.Shopify.currency.active; } catch (e) {}
    return {
      handle: row[1],
      title: row[0],
      url: '/products/' + row[1],
      image: row[2] ? deck.imgBase + row[2] + '?width=300' : '',
      price: Math.round(chosen[1] * 100),
      currency: currency,
      variantId: chosen[0],
      variantsCount: vs.length,
      available: true,
      source: 'deck-index'
    };
  }

  // Handles -> items from the deck index, input order, unknown (sold out) handles skipped.
  function itemsFromDeck(deck, handles, type) {
    var items = [];
    (handles || []).forEach(function (h) {
      var r = deck && deck.map.get(h); if (!r) return;
      var it = itemFromDeckRow(deck, r); if (!it) return;
      if (type) it.recoType = type;
      items.push(it);
    });
    return items;
  }

  // core.w = per link type multipliers learned from what converts (build-graph.mjs);
  // the control bucket keeps neutral weights so the effect stays measurable.
  function useWeights() {
    var T = W.MtgRecoTrack;
    return !(T && T.abBucket() === 'c');
  }
  function weightsFor(core, use) {
    return (use === undefined ? useWeights() : use) ? (core && core.w) || null : null;
  }

  // Graph seeds from the profile (buildProfile): strongest first, glances left out. Without a
  // profile every viewed handle is a seed.
  function profileSeeds(store, profile) {
    return profile ? seedHandles(profile) : Object.keys((store && store.viewed) || {});
  }

  // Graph row items (engine shape + recoType + reasons {en, sr}) from the core + the seeds' shards.
  // [] when anything is missing or fewer than GRAPH_MIN cards qualify.
  function graphItems(core, shards, deckIdx, deck, store, weights, profile) {
    var G = W.MtgRecoGraph;
    if (!core || !G || !deck || !deck.map.size) return [];
    var picked = G.compute(G.mergeShards(core, shards), deckIdx, {
      profileHandles: profileSeeds(store, profile), cartHandles: (store && store.cart) || [],
      limit: GRAPH_LIMIT, min: GRAPH_MIN, weights: weights,
      seedWeights: profile ? seedWeights(profile) : undefined
    });
    var items = [];
    picked.forEach(function (p) {
      var row = deck.map.get(p.handle); if (!row) return;
      var it = itemFromDeckRow(deck, row); if (!it) return;
      it.variantId = p.variantId;
      it.price = Math.round(p.price * 100);
      it.reasons = p.reason;
      it.recoType = p.type;
      items.push(it);
    });
    return items;
  }

  // Sets item.reason from item.reasons for the storefront language ('sr*' -> sr, else en).
  function localize(items, lang) {
    var l = String(lang || 'en').indexOf('sr') === 0 ? 'sr' : 'en';
    (items || []).forEach(function (it) { if (it.reasons) it.reason = it.reasons[l] || it.reasons.en; });
    return items;
  }

  // "Continue" and "Customers also buy" handles; graph cards and the cart are excluded from both.
  function secondaryHandles(profile, pairs, graphList, cartHandles) {
    var exclude = new Set(cartHandles || []);
    (graphList || []).forEach(function (it) { exclude.add(it.handle); });
    var cont = continueRow(profile, { limit: 12 }).filter(function (h) { return !exclude.has(h); });
    var cp = copurchaseRow(profile, pairs || {}, { limit: 12, exclude: exclude }).filter(function (h) { return cont.indexOf(h) < 0; });
    return { cont: cont, cp: cp };
  }

  // --- snapshot of the finished rows (localStorage) ---
  var SNAP_KEY = 'mtgkarte_reco_snap_v1';
  var SNAP_SHOW_MS = 24 * 3600 * 1000; // the page paints a snapshot up to a day old (then refreshes it)
  var SNAP_FRESH_MS = 30 * 60 * 1000;  // warm() leaves a snapshot this young alone if profile + cart match
  var SNAP_REST_MS = 10 * 60 * 1000;   // ...and one this young even when the profile changed (a few more views barely move it)
  var SNAP_GRAPH = 48;                 // graph cards kept (2 pages); deeper "Load more" waits for the fresh list
  var SNAP_MAX_BYTES = 200000;

  // Changes whenever a card is viewed (again) or removed: the snapshot is then recomputed.
  function profileSig(store) {
    var v = (store && store.viewed) || {};
    var viewed = Object.keys(v).sort().map(function (h) { return h + ':' + ((v[h] && v[h].c) || 1); });
    var removed = ((store && store.removed) || []).map(function (e) { return e.h; }).sort();
    return viewed.join(',') + '|' + removed.join(',');
  }
  function cartSig(handles) { return (handles || []).slice().sort().join(','); }

  var SNAP_FIELDS = ['handle', 'title', 'url', 'image', 'price', 'currency', 'variantId', 'variantsCount', 'available', 'source', 'recoType', 'reasons'];
  function snapItem(it) {
    var o = {};
    SNAP_FIELDS.forEach(function (k) { if (it[k] !== undefined) o[k] = it[k]; });
    return o;
  }
  function makeSnap(rows, store, cartHandles, built, now) {
    return {
      v: 1, t: now, sig: profileSig(store), cart: cartSig(cartHandles), built: built || '',
      rows: {
        graph: (rows.graph || []).slice(0, SNAP_GRAPH).map(snapItem),
        cont: (rows.cont || []).map(snapItem),
        cp: (rows.cp || []).map(snapItem)
      }
    };
  }
  // Paintable: right version, not older than maxAge, a graph row of at least GRAPH_MIN cards.
  function snapValid(s, now, maxAge) {
    return !!(s && s.v === 1 && s.rows && Array.isArray(s.rows.graph) && s.rows.graph.length >= GRAPH_MIN &&
      now - s.t >= 0 && now - s.t < (maxAge || SNAP_SHOW_MS));
  }
  // Nothing to recompute. Same cart always (cart cards must not be recommended), and either
  // younger than SNAP_REST_MS with a paintable graph row, or younger than SNAP_FRESH_MS with the
  // same profile. (A snapshot with an empty graph is only kept while the profile is unchanged.)
  function snapFresh(s, store, cartHandles, now) {
    if (!(s && s.v === 1 && now - s.t >= 0 && s.cart === cartSig(cartHandles))) return false;
    var age = now - s.t;
    if (age < SNAP_REST_MS && s.rows && Array.isArray(s.rows.graph) && s.rows.graph.length >= GRAPH_MIN) return true;
    return age < SNAP_FRESH_MS && s.sig === profileSig(store);
  }
  function readSnap(storage) {
    try { var st = storage || W.localStorage; return JSON.parse(st.getItem(SNAP_KEY) || 'null'); } catch (e) { return null; }
  }
  function writeSnap(snap, storage) {
    try {
      var st = storage || W.localStorage, text = JSON.stringify(snap);
      if (text.length > SNAP_MAX_BYTES) return false;
      st.setItem(SNAP_KEY, text);
      return true;
    } catch (e) { return false; }
  }
  // Same first page (handles, in order) -> the fresh list would look the same; no visible swap needed.
  function samePage(a, b, n) {
    n = n || PAGE_SIZE;
    var x = (a || []).slice(0, n), y = (b || []).slice(0, n);
    if (x.length !== y.length) return false;
    for (var i = 0; i < x.length; i++) if (x[i].handle !== y[i].handle) return false;
    return true;
  }

  // --- data loading (no DOM: also runs inside the warm-up Web Worker, see assets/reco-warm.js) ---
  function debug() { try { console.debug.apply(console, ['[reco-page]'].concat([].slice.call(arguments))); } catch (e) {} }
  // Graph core + shards share one 10-minute cache key instead of file_url's ?v=: the page HTML can be
  // cached with an older core ?v= while the shards are already newer, and mismatched builds are dropped.
  function graphBust(u) {
    if (!u) return u;
    var q = u.indexOf('?');
    return (q < 0 ? u : u.slice(0, q)) + '?v=g' + Math.floor(Date.now() / 6e5);
  }

  function fetchJson(url) {
    if (!url) return Promise.resolve(null);
    // sections/reco-page.liquid starts these fetches inline at HTML-parse time and parks
    // the promises on window.MtgRecoPrefetch (keyed by the same URL strings) - reuse them.
    var pre = W.MtgRecoPrefetch;
    if (pre && pre[url]) return pre[url];
    return fetch(url, { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
  }

  var SHARD_TIMEOUT = 4000;
  var GRAPH_TIMEOUT = 8000;
  function withTimeout(p, ms) {
    return new Promise(function (resolve) {
      var t = setTimeout(function () { resolve(null); }, ms);
      p.then(function (v) { clearTimeout(t); resolve(v); }, function () { clearTimeout(t); resolve(null); });
    });
  }

  // Shards the seeds live in (reco-graph-s-NNN.json, NNN = seed id % shards). Missing ones come back null.
  function fetchShards(core, deckIdx, store, shardBase, profile) {
    var G = W.MtgRecoGraph;
    if (!core || !G) return Promise.resolve([]);
    var ids = G.seedIdsFor(core, deckIdx, profileSeeds(store, profile), store.cart || []);
    var nums = G.shardIds(ids, G.shardCount(core));
    if (!shardBase || !nums.length) return Promise.resolve([]);
    return Promise.all(nums.map(function (n) { return withTimeout(fetchJson(G.shardUrl(shardBase, n)), SHARD_TIMEOUT); }))
      .then(function (shards) {
        var missing = shards.filter(function (x) { return !x; }).length;
        if (missing) debug('graph shards missing: ' + missing + '/' + shards.length);
        return shards;
      });
  }

  // The heavy half of warm(): fetches the data files and computes the snapshot rows.
  // job = { cfg: { deckIndex, recoGraph, recoGraphShard, copurchase } (absolute URLs),
  //         store: { viewed, removed, cart }, now, useWeights, currency }  (currency: the page's active
  //         one - a worker has no window.Shopify)
  // Resolves { rows: { graph, cont, cp } (snapshot fields only), built } or null when data is missing.
  // Structured-clone safe in and out, so the warm-up can run it in a Web Worker.
  function warmRows(job) {
    var cfg = job.cfg, store = job.store, now = job.now;
    var coreP = withTimeout(fetchJson(graphBust(cfg.recoGraph)), GRAPH_TIMEOUT);
    return Promise.all([fetchJson(cfg.deckIndex), coreP, fetchJson(cfg.copurchase)]).then(function (r) {
      var deckIdx = r[0], core = r[1];
      if (!deckIdx || !deckIdx.rows || !core) return null;
      var profile = buildProfile(store, now);
      return fetchShards(core, deckIdx, store, graphBust(cfg.recoGraphShard), profile).then(function (shards) {
        var deck = buildDeckLookup(deckIdx);
        var graph = shards.length ? graphItems(core, shards, deckIdx, deck, store, weightsFor(core, job.useWeights), profile) : [];
        var h = secondaryHandles(profile, (r[2] && r[2].pairs) || {}, graph, store.cart);
        var out = function (it) { var o = snapItem(it); if (job.currency) o.currency = job.currency; return o; };
        return {
          rows: {
            graph: graph.slice(0, SNAP_GRAPH).map(out),
            cont: itemsFromDeck(deck, h.cont, 'continue').map(out),
            cp: itemsFromDeck(deck, h.cp, 'copurchase').map(out)
          },
          built: core.built || ''
        };
      });
    });
  }

  W.MtgRecoPage = {
    warmRows: warmRows,
    PAGE_SIZE: PAGE_SIZE,
    GRAPH_LIMIT: GRAPH_LIMIT,
    GRAPH_MIN: GRAPH_MIN,
    INTEREST_MIN: INTEREST_MIN,
    itemWeight: itemWeight,
    buildProfile: buildProfile,
    seedHandles: seedHandles,
    seedWeights: seedWeights,
    copurchaseRow: copurchaseRow,
    continueRow: continueRow,
    pager: pager,
    buildDeckLookup: buildDeckLookup,
    itemFromDeckRow: itemFromDeckRow,
    itemsFromDeck: itemsFromDeck,
    graphItems: graphItems,
    localize: localize,
    secondaryHandles: secondaryHandles,
    SNAP_KEY: SNAP_KEY,
    SNAP_SHOW_MS: SNAP_SHOW_MS,
    SNAP_FRESH_MS: SNAP_FRESH_MS,
    SNAP_REST_MS: SNAP_REST_MS,
    SNAP_GRAPH: SNAP_GRAPH,
    profileSig: profileSig,
    cartSig: cartSig,
    makeSnap: makeSnap,
    snapValid: snapValid,
    snapFresh: snapFresh,
    readSnap: readSnap,
    writeSnap: writeSnap,
    samePage: samePage
  };

  if (typeof document === 'undefined') return;

  // Resolves handles to card items from the deck index, in input order, skipping
  // handles that are not in it (sold out since the last hourly build). Falls back to
  // the engine's per-product hydration only when the deck index failed to load.
  function resolveHandles(deck, handles) {
    if (!handles.length) return Promise.resolve([]);
    if (!deck || !deck.map.size) return W.MtgReco.hydrateHandles(handles, { concurrency: 6 });
    return Promise.resolve(itemsFromDeck(deck, handles));
  }

  function typed(items, type) {
    items.forEach(function (it) { it.recoType = type; });
    return items;
  }

  function cartHandlesOf(cart) {
    var out = [];
    ((cart && cart.items) || []).forEach(function (i) { if (i.handle && out.indexOf(i.handle) < 0) out.push(i.handle); });
    return out;
  }
  function profileEntries(store) {
    return Object.keys(store.viewed || {}).length + (store.removed || []).length + (store.cart || []).length;
  }

  // viewed entries + `i` (current interest) when the tracker can score them
  function withInterest(S, viewed, now) {
    if (!S || typeof S.interest !== 'function') return viewed;
    var out = {};
    Object.keys(viewed || {}).forEach(function (h) {
      var e = viewed[h], copy = {};
      Object.keys(e).forEach(function (k) { copy[k] = e[k]; });
      copy.i = S.interest(e, now);
      out[h] = copy;
    });
    return out;
  }

  // Profile store { viewed (+ interest), removed } once a logged-in customer's server profile
  // (other devices) is in - store.ready() waits for it, with its own timeout.
  function readStore(now) {
    var S = W.MtgReco && W.MtgReco.store;
    if (!S) return Promise.resolve({ viewed: {}, removed: [] });
    return Promise.resolve(S.ready ? S.ready() : null).then(function () {
      return { viewed: withInterest(S, S.getViewed(), now), removed: S.getRemoved() };
    });
  }

  function PageController(root) {
    this.root = root; this.t = root.dataset; this.rows = {}; this.fellBack = false;
    this.lang = this.t.lang || 'en';
    this.fromSnap = false; // rows came from the snapshot (painted inline, adopted here)
    this.touched = false;  // visitor clicked something: the fresh list no longer replaces what is shown
  }

  PageController.prototype.start = function () {
    var self = this;
    try { performance.mark('reco:start'); } catch (e) {}
    try {
      var store = { viewed: {}, removed: [] };
      var now = Date.now();
      this.root.addEventListener('click', function () { self.touched = true; }, true);
      // Snapshot painted by the inline script in sections/reco-page.liquid: adopt those rows and wire
      // them up right away; the fresh computation below then only refreshes them.
      if (this.root.dataset.snapPainted === '1') this.showSnap(readSnap());
      // Cart contents are part of the profile (strongest signal), so fetch the
      // cart first and only then decide between the personal page and the fallback.
      Promise.all([fetchJson('/cart.js'), readStore(now)])
        .then(function (r) {
          var cart = r[0];
          store = r[1];
          var cartHandles = cartHandlesOf(cart);
          store.cart = cartHandles;
          if (profileEntries(store) < 3) { if (!self.fromSnap) self.renderFallback(); return; }
          self.profile = buildProfile(store, now);
          // Rows are created up front (hidden) so they keep this order no matter which
          // data arrives first: graph, continue, copurchase. Each shows as soon as it is ready.
          if (!self.fromSnap) {
            self.rows.graph = self.makeRow(self.t.tGraph || 'Goes with your cards', 'graph');
            self.rows.cont = self.makeRow(self.t.tContinue, 'continue');
            self.rows.cp = self.makeRow(self.t.tCopurchase, 'copurchase');
          }
          var deckP = fetchJson(self.t.deckIndex).then(function (idx) { self.deck = buildDeckLookup(idx); return idx; });
          var coreP = withTimeout(fetchJson(graphBust(self.t.recoGraph)), GRAPH_TIMEOUT);
          var cpP = fetchJson(self.t.copurchase);
          var built = '';
          // Graph row: shown the moment it is computed - does not wait for the other rows.
          var graphP = Promise.all([deckP, coreP])
            .then(function (r) { built = (r[1] && r[1].built) || ''; return self.graphRow(r[1], r[0], store); })
            .then(function (items) {
              try { performance.mark('reco:scored'); } catch (e) {}
              if (items.length < GRAPH_MIN) {
                if (!self.fromSnap) self.renderFallback(self.rows.graph);
                return [];
              }
              self.showGraph(items);
              return items;
            });
          // Secondary rows: need the graph handles (excluded from them) and the copurchase pairs.
          return Promise.all([graphP, cpP, deckP])
            .then(function (r) { return self.renderSecondaryRows(r[0], r[1] || { pairs: {} }, cartHandles); })
            .then(function (sec) {
              // The next visit paints this at once (only with the deck index: the items come from it).
              if (!self.deck || !self.deck.map.size) return;
              writeSnap(makeSnap({ graph: self.freshGraph || [], cont: sec.cont, cp: sec.cp }, store, cartHandles, built, Date.now()));
            });
        })
        .catch(function (e) { debug('page failed', e); if (!self.fromSnap) self.renderFallback(); });
    } catch (e) {
      debug('start failed', e);
      return self.fromSnap ? undefined : self.renderFallback();
    }
  };

  // Rows from the snapshot: adopts the inline-painted rows (makeRow finds them) and renders the
  // snapshot items through <cart-recommendations>, which wires add-to-cart, clicks and impressions.
  PageController.prototype.showSnap = function (snap) {
    if (!snapValid(snap, Date.now(), SNAP_SHOW_MS)) return;
    this.fromSnap = true;
    this.rows.graph = this.makeRow(this.t.tGraph || 'Goes with your cards', 'graph');
    this.rows.cont = this.makeRow(this.t.tContinue, 'continue');
    this.rows.cp = this.makeRow(this.t.tCopurchase, 'copurchase');
    this.graphItems = localize(snap.rows.graph, this.lang);
    this.graphPager = pager(this.graphItems, PAGE_SIZE);
    this.fillRow(this.rows.graph, this.graphPager.next(), false);
    this.fillRow(this.rows.cont, snap.rows.cont || [], false);
    this.fillRow(this.rows.cp, snap.rows.cp || [], false);
    this.hideSkeleton();
    this.setupLoadMore();
  };

  // Fresh graph list. With a snapshot on screen it replaces the shown cards only while the page is
  // untouched and the cards on screen differ; otherwise "Load more" just continues from the fresh list.
  PageController.prototype.showGraph = function (items) {
    this.freshGraph = items;
    if (!this.fromSnap) {
      this.graphItems = items;
      this.graphPager = pager(items, PAGE_SIZE);
      this.fillRow(this.rows.graph, this.graphPager.next(), false);
      this.hideSkeleton();
      this.setupLoadMore();
      return;
    }
    if (this.touched) return;
    var shown = this.graphPager ? this.graphPager.shown() : PAGE_SIZE;
    var fresh = pager(items, PAGE_SIZE);
    if (samePage(this.graphItems, items, shown)) {
      while (fresh.shown() < shown && !fresh.done()) fresh.next(); // same cards on screen: keep them
    } else {
      this.fillRow(this.rows.graph, fresh.next(), false);
    }
    this.graphItems = items;
    this.graphPager = fresh;
    this.rows.graph.more.hidden = fresh.done();
  };

  // Resolves to { cont, cp } items (also saved in the snapshot).
  PageController.prototype.renderSecondaryRows = function (graphList, cp, cartHandles) {
    var self = this;
    var h = secondaryHandles(self.profile, cp.pairs || {}, graphList, cartHandles);
    var contP = resolveHandles(self.deck, h.cont).then(function (items) { return typed(self.withSeenLabels(items), 'continue'); }).catch(function (e) { debug('continue row failed', e); return []; });
    var cpP = resolveHandles(self.deck, h.cp).then(function (items) { return typed(items, 'copurchase'); }).catch(function (e) { debug('copurchase row failed', e); return []; });
    return Promise.all([contP, cpP]).then(function (r) {
      if (!(self.fromSnap && self.touched)) {
        self.fillRow(self.rows.cont, r[0], false);
        self.fillRow(self.rows.cp, r[1], false);
      }
      return { cont: r[0], cp: r[1] };
    });
  };

  // "Goes with your cards": up to GRAPH_LIMIT cards scored from the reco-graph core + the shards
  // the seeds live in, for seeds = viewed handles ∪ cart. Resolves to card items in the engine
  // shape plus `reason` (localized) - or [] when the core is missing, the helper did not load,
  // the deck index is missing, or fewer than GRAPH_MIN cards qualify. Never rejects.
  PageController.prototype.graphRow = function (core, deckIdx, store) {
    var self = this;
    try {
      if (!core || !W.MtgRecoGraph || !this.deck || !this.deck.map.size) return Promise.resolve([]);
      return fetchShards(core, deckIdx, store, graphBust(this.t.recoGraphShard), self.profile)
        .then(function (shards) {
          if (!shards.length) return [];
          return localize(graphItems(core, shards, deckIdx, self.deck, store, weightsFor(core), self.profile), self.lang);
        })
        .catch(function (e) { debug('graph row failed', e); return []; });
    } catch (e) { debug('graph row failed', e); return Promise.resolve([]); }
  };

  // "Viewed several times" / "Was in your cart" under cards from the profile. The row title
  // already says "continue where you left off", so plain "Viewed before" is left out there.
  PageController.prototype.withSeenLabels = function (items) {
    var S = W.MtgReco && W.MtgReco.store;
    if (!S || typeof S.seenLabel !== 'function' || !this.profile) return items;
    var lang = this.lang, byHandle = {};
    this.profile.items.forEach(function (i) { byHandle[i.handle] = i; });
    var plain = S.seenLabel('viewed', { c: 1, t: Date.now(), s: 0.3 }, lang);
    items.forEach(function (it) {
      var p = byHandle[it.handle]; if (!p) return;
      var label = p.kind === 'removed' ? S.seenLabel('removed', null, lang)
        : S.seenLabel('viewed', { c: p.c || 1, t: p.t, s: typeof p.i === 'number' ? p.i : undefined, d: p.d }, lang, p.t);
      if (label && label !== plain) it.reason = label;
    });
    return items;
  };

  PageController.prototype.hideSkeleton = function () {
    try { performance.mark('reco:rendered'); } catch (e) {}
    var sk = this.root.querySelector('[data-skeleton]'); if (sk) sk.hidden = true;
    var ld = this.root.querySelector('[data-loading]'); if (ld) ld.hidden = true;
  };

  // `key` = row name in the measurement events (reco-track.js). A row the inline snapshot
  // painter already drew (data-snap-row="<key>") is adopted instead of creating a new one.
  PageController.prototype.makeRow = function (title, key) {
    var node = key ? this.root.querySelector('[data-snap-row="' + key + '"]') : null;
    if (!node) {
      var tpl = this.root.querySelector('[data-row-template]');
      node = tpl.content.firstElementChild.cloneNode(true);
      node.querySelector('.reco-cart__heading').textContent = title;
      this.root.appendChild(node);
    }
    var el = node.querySelector('cart-recommendations');
    if (key) el.dataset.recoRow = key;
    return { section: node, el: el, more: node.querySelector('.reco-cart__more') };
  };

  // Renders `items` (already resolved) into one row. An empty initial fill hides the
  // row; an empty append (Load more) never un-hides or hides anything.
  PageController.prototype.fillRow = function (row, items, append) {
    if (!items.length) { if (!append) row.section.hidden = true; return; }
    row.section.hidden = false;
    if (append) row.el.setItemsAppend(items); else row.el.setItems(items);
  };

  // "Load more" on the graph row: next PAGE_SIZE cards from the list already computed
  // (this.graphPager, swapped for the fresh list when it arrives) - no network calls.
  PageController.prototype.setupLoadMore = function () {
    var self = this; var btn = this.rows.graph.more;
    btn.textContent = this.t.tMore; btn.hidden = this.graphPager.done();
    if (this.loadMoreWired) return;
    this.loadMoreWired = true;
    btn.addEventListener('click', function () {
      self.fillRow(self.rows.graph, self.graphPager.next(), true);
      btn.hidden = self.graphPager.done();
    });
  };

  // "Popular" (fallback collection, /collections/<handle>/products.json): for visitors
  // without a profile, and whenever the graph row cannot be shown. `row` = an existing
  // row section to reuse (keeps the top position), otherwise a new one is appended.
  PageController.prototype.renderFallback = function (row) {
    var self = this;
    if (this.fellBack) return Promise.resolve();
    this.fellBack = true;
    row = row || this.makeRow(this.t.tPopular, 'popular');
    row.el.dataset.recoRow = 'popular';
    row.section.querySelector('.reco-cart__heading').textContent = this.t.tPopular;
    var note = row.section.querySelector('.reco-page__note'); note.textContent = this.t.tPopularNote; note.hidden = false;
    return W.MtgReco.collectionItems(this.t.fallbackCollection || 'commander-staples', 48).then(function (items) {
      row.section.hidden = !items.length; row.el.setItems(items);
      self.hideSkeleton();
    }).catch(function (e) { debug('fallback failed', e); self.hideSkeleton(); });
  };

  // Warm-up on other pages (assets/reco-warm.js): computes the rows the page would show and saves
  // them as the snapshot, so /pages/preporuceno paints them the moment its HTML arrives.
  // cfg = { deckIndex, recoGraph, recoGraphShard, copurchase } (file URLs), plus optional
  //   onStart()     called once the heavy part begins (reco-warm.js stamps its throttle there)
  //   worker(job)   runs warmRows(job) off the main thread -> Promise; rejects when the worker cannot
  //                 run at all, and then warmRows() runs here instead
  // Resolves true when a snapshot was written, false when nothing was needed or anything failed. Never rejects.
  var warming = null;
  function currentCurrency() { try { return (W.Shopify && W.Shopify.currency && W.Shopify.currency.active) || ''; } catch (e) { return ''; } }
  function absUrl(u) { try { return u ? new URL(u, W.location.href).href : u; } catch (e) { return u; } }
  function warm(cfg) {
    if (warming) return warming;
    warming = Promise.resolve().then(function () {
      var R = W.MtgReco && W.MtgReco.store;
      if (!R || !cfg || !cfg.deckIndex || !cfg.recoGraph) return false;
      var now = Date.now();
      // interest is scored here (the worker has no tracker); the job carries it in store.viewed[h].i
      return Promise.all([fetchJson('/cart.js'), readStore(now)]).then(function (r) {
        var store = r[1];
        store.cart = cartHandlesOf(r[0]);
        if (profileEntries(store) < 3 || snapFresh(readSnap(), store, store.cart, now)) return false;
        if (cfg.onStart) cfg.onStart();
        var job = {
          cfg: { deckIndex: absUrl(cfg.deckIndex), recoGraph: absUrl(cfg.recoGraph), recoGraphShard: absUrl(cfg.recoGraphShard), copurchase: absUrl(cfg.copurchase) },
          store: store, now: now, useWeights: useWeights(), currency: currentCurrency()
        };
        var res = cfg.worker
          ? cfg.worker(job).catch(function (e) { debug('warm worker unavailable, computing here', e); return warmRows(job); })
          : warmRows(job);
        return res.then(function (out) {
          return !!(out && out.rows) && writeSnap(makeSnap(out.rows, store, store.cart, out.built, now));
        });
      });
    }).catch(function (e) { debug('warm failed', e); return false; })
      .then(function (v) { warming = null; return v; });
    return warming;
  }
  W.MtgRecoPage.warm = warm;

  function boot() {
    try {
      var root = document.getElementById('reco-page'); if (!root) return;
      var tries = 0;
      (function wait() {
        if (W.MtgReco && W.MtgReco.hydrateHandles && W.customElements.get('cart-recommendations')) return new PageController(root).start();
        if (++tries > 100) {
          debug('dependencies never loaded');
          var sk = root.querySelector('[data-skeleton]'); if (sk) sk.hidden = true;
          var ld = root.querySelector('[data-loading]'); if (ld) ld.hidden = true;
          return;
        }
        setTimeout(wait, 50);
      })();
    } catch (e) {
      debug('boot failed', e);
      return;
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
